/**
 * Marketing Analytics Hook
 * Tracks visitor behavior and engagement on marketing pages
 */

import { useCallback, useEffect, useRef } from 'react';
import { MarketingVariant } from '../routes/MarketingRouter';

interface PageViewData {
  path: string;
  variant: MarketingVariant;
  referrer?: string;
  timestamp: string;
  sessionId?: string;
  userId?: string;
}

interface EventData {
  category: string;
  action: string;
  label?: string;
  value?: number;
  variant?: MarketingVariant;
  metadata?: Record<string, any>;
}

interface EngagementMetrics {
  timeOnPage: number;
  scrollDepth: number;
  interactions: number;
  videoWatched?: number;
  formEngagement?: boolean;
}

export const useMarketingAnalytics = () => {
  const sessionId = useRef<string>('');
  const pageStartTime = useRef<number>(Date.now());
  const maxScrollDepth = useRef<number>(0);
  const interactionCount = useRef<number>(0);
  const engagementTimer = useRef<NodeJS.Timeout>();

  // Generate or retrieve session ID
  useEffect(() => {
    let existingSessionId = sessionStorage.getItem('sigma08_session_id');

    if (!existingSessionId) {
      existingSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('sigma08_session_id', existingSessionId);
    }

    sessionId.current = existingSessionId;
  }, []);

  /**
   * Track page view
   */
  const trackPageView = useCallback((data: PageViewData) => {
    // Reset metrics for new page
    pageStartTime.current = Date.now();
    maxScrollDepth.current = 0;
    interactionCount.current = 0;

    // Add session ID
    const enrichedData = {
      ...data,
      sessionId: sessionId.current,
      userId: localStorage.getItem('sigma08_user_id') || undefined,
    };

    // Send to Google Analytics if available
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: data.path,
        page_location: window.location.href,
        page_referrer: data.referrer,
        custom_dimension_1: data.variant,
        custom_dimension_2: sessionId.current,
      });
    }

    // Send to internal analytics API
    sendToAnalyticsAPI('pageview', enrichedData);

    // Track scroll depth
    trackScrollDepth();

    // Track time on page
    startEngagementTracking();
  }, []);

  /**
   * Track custom events
   */
  const trackEvent = useCallback((data: EventData) => {
    interactionCount.current++;

    const enrichedData = {
      ...data,
      sessionId: sessionId.current,
      timestamp: new Date().toISOString(),
      pageUrl: window.location.href,
    };

    // Send to Google Analytics
    if (window.gtag) {
      window.gtag('event', data.action, {
        event_category: data.category,
        event_label: data.label,
        value: data.value,
        custom_dimension_1: data.variant,
        ...data.metadata,
      });
    }

    // Send to internal analytics
    sendToAnalyticsAPI('event', enrichedData);
  }, []);

  /**
   * Track form interactions
   */
  const trackFormInteraction = useCallback(
    (formName: string, field: string, action: 'focus' | 'blur' | 'change' | 'submit') => {
      trackEvent({
        category: 'Form',
        action: action,
        label: `${formName}:${field}`,
        metadata: {
          formName,
          field,
          timestamp: Date.now(),
        },
      });
    },
    [trackEvent]
  );

  /**
   * Track CTA clicks
   */
  const trackCTAClick = useCallback(
    (ctaName: string, location: string, variant?: MarketingVariant) => {
      trackEvent({
        category: 'CTA',
        action: 'click',
        label: ctaName,
        variant,
        metadata: {
          location,
          ctaText: ctaName,
          pageSection: location,
        },
      });
    },
    [trackEvent]
  );

  /**
   * Track video engagement
   */
  const trackVideoEngagement = useCallback(
    (videoId: string, action: 'play' | 'pause' | 'complete', progress?: number) => {
      trackEvent({
        category: 'Video',
        action: action,
        label: videoId,
        value: progress,
        metadata: {
          videoId,
          progress,
          timestamp: Date.now(),
        },
      });
    },
    [trackEvent]
  );

  /**
   * Track conversion funnel steps
   */
  const trackFunnelStep = useCallback(
    (funnelName: string, step: number, stepName: string) => {
      trackEvent({
        category: 'Funnel',
        action: stepName,
        label: funnelName,
        value: step,
        metadata: {
          funnelName,
          step,
          stepName,
          timestamp: Date.now(),
        },
      });
    },
    [trackEvent]
  );

  /**
   * Track A/B test impressions
   */
  const trackABTestImpression = useCallback(
    (testName: string, variant: string) => {
      trackEvent({
        category: 'ABTest',
        action: 'impression',
        label: testName,
        metadata: {
          testName,
          testVariant: variant,
          timestamp: Date.now(),
        },
      });

      // Store in session for consistent experience
      sessionStorage.setItem(`ab_test_${testName}`, variant);
    },
    [trackEvent]
  );

  /**
   * Track scroll depth
   */
  const trackScrollDepth = useCallback(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercentage = Math.round(((scrollTop + windowHeight) / documentHeight) * 100);

      if (scrollPercentage > maxScrollDepth.current) {
        maxScrollDepth.current = scrollPercentage;

        // Track milestones
        if ([25, 50, 75, 90, 100].includes(scrollPercentage)) {
          trackEvent({
            category: 'Engagement',
            action: 'scroll',
            label: `${scrollPercentage}%`,
            value: scrollPercentage,
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [trackEvent]);

  /**
   * Track engagement over time
   */
  const startEngagementTracking = useCallback(() => {
    // Clear existing timer
    if (engagementTimer.current) {
      clearInterval(engagementTimer.current);
    }

    // Track engagement every 30 seconds
    engagementTimer.current = setInterval(() => {
      const timeOnPage = Math.round((Date.now() - pageStartTime.current) / 1000);

      trackEvent({
        category: 'Engagement',
        action: 'time_on_page',
        label: `${Math.floor(timeOnPage / 30) * 30}s`,
        value: timeOnPage,
        metadata: {
          scrollDepth: maxScrollDepth.current,
          interactions: interactionCount.current,
        },
      });
    }, 30000);

    // Clean up on unmount
    return () => {
      if (engagementTimer.current) {
        clearInterval(engagementTimer.current);
      }
    };
  }, [trackEvent]);

  /**
   * Track exit intent
   */
  const trackExitIntent = useCallback(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        const timeOnPage = Math.round((Date.now() - pageStartTime.current) / 1000);

        trackEvent({
          category: 'Engagement',
          action: 'exit_intent',
          label: window.location.pathname,
          value: timeOnPage,
          metadata: {
            scrollDepth: maxScrollDepth.current,
            interactions: interactionCount.current,
            timeOnPage,
          },
        });
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [trackEvent]);

  /**
   * Get engagement metrics
   */
  const getEngagementMetrics = useCallback((): EngagementMetrics => {
    return {
      timeOnPage: Math.round((Date.now() - pageStartTime.current) / 1000),
      scrollDepth: maxScrollDepth.current,
      interactions: interactionCount.current,
    };
  }, []);

  /**
   * Send data to analytics API
   */
  const sendToAnalyticsAPI = async (type: string, data: any) => {
    try {
      // Queue analytics data if offline
      if (!navigator.onLine) {
        const queue = JSON.parse(localStorage.getItem('analytics_queue') || '[]');
        queue.push({ type, data, timestamp: Date.now() });
        localStorage.setItem('analytics_queue', JSON.stringify(queue));
        return;
      }

      // Send to analytics endpoint
      await fetch('/api/analytics/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type,
          data,
          timestamp: new Date().toISOString(),
        }),
      });

      // Process queued events if any
      const queue = JSON.parse(localStorage.getItem('analytics_queue') || '[]');
      if (queue.length > 0) {
        await Promise.all(
          queue.map((item: any) =>
            fetch('/api/analytics/track', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(item),
            })
          )
        );
        localStorage.removeItem('analytics_queue');
      }
    } catch (error) {
      console.debug('Analytics tracking failed:', error);
    }
  };

  // Set up exit intent tracking
  useEffect(() => {
    const cleanup = trackExitIntent();
    return cleanup;
  }, [trackExitIntent]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (engagementTimer.current) {
        clearInterval(engagementTimer.current);
      }

      // Send final engagement metrics
      const metrics = getEngagementMetrics();
      sendToAnalyticsAPI('engagement', {
        ...metrics,
        sessionId: sessionId.current,
        pageUrl: window.location.href,
      });
    };
  }, [getEngagementMetrics]);

  return {
    trackPageView,
    trackEvent,
    trackFormInteraction,
    trackCTAClick,
    trackVideoEngagement,
    trackFunnelStep,
    trackABTestImpression,
    getEngagementMetrics,
  };
};
